import { useMemo, useState } from 'react';
import { Search, Loader2, SlidersHorizontal, ChevronDown, RectangleHorizontal } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { useMerchantMarket } from '../hooks/useMerchantMarket';
import useResponsivePlateWidth from '../hooks/useResponsivePlateWidth';
import MarketHero from '../components/marketplace/MarketHero';
import MarketFilterShell, { FilterField } from '../components/marketplace/MarketFilterShell';
import EmptyState from '../components/marketplace/EmptyState';
import PlateCard, { EMIRATES } from '../components/marketplace/PlateCard';
import { pageBg, inputCls, selectCls } from '../components/marketplace/frames';

export default function CarPlatesPage() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const { items, loading, error } = useMerchantMarket('car_plates', { limit: 300 });
  const plateWidth = useResponsivePlateWidth({ mobile: 140, tablet: 230, desktop: 280 });

  const [q, setQ] = useState('');
  const [emirate, setEmirate] = useState('all');
  const [plateType, setPlateType] = useState('all');
  const [sort, setSort] = useState('newest');
  const [advOpen, setAdvOpen] = useState(false);
  const [prefix, setPrefix] = useState('');
  const [suffix, setSuffix] = useState('');

  const filtered = useMemo(() => {
    let list = items;
    if (emirate !== 'all') list = list.filter((i) => i.emirate === emirate);
    if (plateType !== 'all') list = list.filter((i) => i.plateType === plateType);
    if (q) {
      list = list.filter(
        (i) =>
          (i.plateNumber || '').includes(q) ||
          (i.displayPlateNumber || '').includes(q) ||
          (i.plateCode || '').toLowerCase().includes(q.toLowerCase()) ||
          (i.store?.name || '').toLowerCase().includes(q.toLowerCase())
      );
    }
    if (prefix) {
      const p = prefix.replace(/\D/g, '');
      list = list.filter((i) => String(i.plateNumber || i.displayPlateNumber || '').replace(/\D/g, '').startsWith(p));
    }
    if (suffix) {
      const s = suffix.replace(/\D/g, '');
      list = list.filter((i) => String(i.plateNumber || i.displayPlateNumber || '').replace(/\D/g, '').endsWith(s));
    }
    if (sort === 'newest')
      list = [...list].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    else if (sort === 'price_asc')
      list = [...list].filter((i) => !i.priceOnRequest).sort((a, b) => (a.price || 0) - (b.price || 0));
    else if (sort === 'price_desc')
      list = [...list].filter((i) => !i.priceOnRequest).sort((a, b) => (b.price || 0) - (a.price || 0));
    return list;
  }, [items, q, emirate, plateType, sort, prefix, suffix]);

  const hasAnyFilter = q || emirate !== 'all' || plateType !== 'all' || sort !== 'newest' || prefix || suffix;
  const clearAll = () => {
    setQ(''); setEmirate('all'); setPlateType('all'); setSort('newest');
    setPrefix(''); setSuffix('');
  };
  const inp = inputCls(isDarkMode);
  const sel = selectCls(isDarkMode);

  return (
    <div className={`min-h-screen ${pageBg(isDarkMode)}`} dir={isAr ? 'rtl' : 'ltr'}>
      <MarketHero
        isDarkMode={isDarkMode}
        isAr={isAr}
        icon={RectangleHorizontal}
        eyebrowAr="لوحات معتمدة بالتصميم الرسمي لكل إمارة"
        eyebrowEn="Verified plates in each emirate's official design"
        titleAr="لوحات مركبات"
        titleAccentAr="فاخرة ومميزة"
        titleEn="Premium Car"
        titleAccentEn="Plates — VIP"
        subtitleAr="مجموعة حصرية من لوحات جميع إمارات الدولة — أرقام قصيرة، ترقيمات نادرة، وتصاميم رسمية حية بأسعار تنافسية."
        subtitleEn="Exclusive VIP plates across all UAE Emirates — short numbers, rare series and live official designs."
        aiPromptAr="ما هي لوحات السيارات المميزة المتاحة حالياً وأسعارها؟"
        aiPromptEn="Which premium car plates are available right now and at what prices?"
        stats={[
          { value: items.length || '—', label: isAr ? 'لوحة متاحة' : 'Available', icon: <RectangleHorizontal className="h-4 w-4" /> },
          { value: '7', label: isAr ? 'إمارات' : 'Emirates', accent: '#F5A962' },
          { value: '✓', label: isAr ? 'موثّق' : 'Verified', accent: '#1E9BD9' },
        ]}
      />

      <MarketFilterShell
        isDarkMode={isDarkMode}
        isAr={isAr}
        count={filtered.length}
        total={items.length}
        unitAr="لوحة"
        unitEn="plates"
        hasFilter={hasAnyFilter}
        onClear={clearAll}
        testId="plates-filters"
      >
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute top-1/2 -translate-y-1/2 start-3 h-4 w-4 text-[#1E9BD9]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={isAr ? 'ابحث برقم اللوحة أو الكود أو المتجر…' : 'Search plate number, code or store…'} className={`h-11 ps-9 rounded-xl ${inp}`} data-testid="plates-search" />
          </div>
          <select value={emirate} onChange={(e) => setEmirate(e.target.value)} className={sel} data-testid="plates-emirate">
            <option value="all">{isAr ? 'كل الإمارات' : 'All Emirates'}</option>
            {Object.entries(EMIRATES).map(([k, v]) => <option key={k} value={k}>{isAr ? v.ar : v.en}</option>)}
          </select>
          <select value={plateType} onChange={(e) => setPlateType(e.target.value)} className={sel} data-testid="plates-type">
            <option value="all">{isAr ? 'كل الأنواع' : 'All Types'}</option>
            <option value="private">{isAr ? 'خصوصي' : 'Private'}</option>
            <option value="commercial">{isAr ? 'تجاري' : 'Commercial'}</option>
            <option value="vip">VIP</option>
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className={sel} data-testid="plates-sort">
            <option value="newest">{isAr ? 'الأحدث' : 'Newest'}</option>
            <option value="price_asc">{isAr ? 'الأرخص أولاً' : 'Price ↑'}</option>
            <option value="price_desc">{isAr ? 'الأغلى أولاً' : 'Price ↓'}</option>
          </select>
          <Button
            type="button"
            onClick={() => setAdvOpen((v) => !v)}
            className={`h-11 rounded-xl font-semibold whitespace-nowrap ${
              advOpen
                ? 'bg-gradient-to-r from-[#1E9BD9] to-[#F5A962] text-white hover:opacity-90'
                : isDarkMode
                  ? 'bg-white/5 text-white border border-[#1E9BD9]/20 hover:bg-[#1E9BD9]/10'
                  : 'bg-white text-slate-700 border border-[#1E9BD9]/25 hover:bg-[#1E9BD9]/5'
            }`}
            data-testid="plates-advanced-toggle"
          >
            <SlidersHorizontal className="h-4 w-4 me-1.5" />
            {isAr ? 'متقدم' : 'More'}
            <ChevronDown className={`h-4 w-4 ms-1.5 transition-transform ${advOpen ? 'rotate-180' : ''}`} />
          </Button>
        </div>

        {advOpen && (
          <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FilterField label={isAr ? 'يبدأ بـ' : 'Starts with'} isDarkMode={isDarkMode}>
                <Input inputMode="numeric" value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder="1" className={`h-10 rounded-lg ${inp}`} data-testid="plates-prefix" />
              </FilterField>
              <FilterField label={isAr ? 'ينتهي بـ' : 'Ends with'} isDarkMode={isDarkMode}>
                <Input inputMode="numeric" value={suffix} onChange={(e) => setSuffix(e.target.value)} placeholder="8" className={`h-10 rounded-lg ${inp}`} data-testid="plates-suffix" />
              </FilterField>
            </div>
          </div>
        )}
      </MarketFilterShell>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-20">
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#1E9BD9]" /></div>
        ) : error ? (
          <p className="text-center text-red-500 py-24">{error}</p>
        ) : filtered.length === 0 ? (
          <EmptyState isDarkMode={isDarkMode} icon={RectangleHorizontal} title={isAr ? 'لا توجد لوحات مطابقة' : 'No matching plates'} hint={isAr ? 'جرّب تغيير الفلاتر أو البحث بكلمات أخرى' : 'Try adjusting filters or search terms'} />
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5" data-testid="plates-grid">
            {filtered.map((it, idx) => (
              <PlateCard key={it.id} item={it} index={idx} isDarkMode={isDarkMode} isAr={isAr} plateWidth={plateWidth} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
